import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { CreditCard, Heart, Users } from 'lucide-react';

export default function PaymentPage({ auth, onPaymentComplete }) {
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/payment/signup`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Payment successful! Welcome to the 12 Challenges!');
      onPaymentComplete();
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 glass" data-testid="payment-card">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Complete Your Sign-Up</h1>
          <p className="text-gray-600">One final step to join the 12 Challenges community</p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl border-2 border-teal-200 mb-8">
          <div className="text-center mb-6">
            <div className="text-6xl font-bold text-teal-600 mb-2">£20</div>
            <p className="text-gray-700 font-medium">One-time sign-up fee</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Users className="text-teal-600 mt-1" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Join the Community</p>
                <p className="text-sm text-gray-600">
                  Become part of a supportive community challenging themselves throughout 2026
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Heart className="text-teal-600 mt-1" size={24} />
              <div>
                <p className="font-semibold text-gray-800">100% Community Funded</p>
                <p className="text-sm text-gray-600">
                  This payment is fully reinvested in the community. No organizers make any profit from the 12 Challenges.
                  All funds go towards maintaining the platform and supporting the community.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CreditCard className="text-teal-600 mt-1" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Secure Payment</p>
                <p className="text-sm text-gray-600">
                  Your payment information is processed securely. This is a one-time payment for lifetime access to 2026 challenges.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 italic">
            "The 12 Challenges is a non-profit initiative created by and for the community. 
            Every penny goes back into making this experience better for everyone."
          </p>
        </div>

        <Button
          onClick={handlePayment}
          disabled={loading}
          className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-lg py-6"
          data-testid="payment-button"
        >
          {loading ? 'Processing Payment...' : 'Complete Payment (£20)'}
        </Button>

        <p className="text-xs text-center text-gray-500 mt-4">
          By completing this payment, you agree to the 12 Challenges terms and conditions.
        </p>
      </Card>
    </div>
  );
}
